import React from 'react';
import {
	Box,
	Card,
	CardActionArea,
	CardContent,
	Chip,
	Stack,
	Typography,
} from '@mui/material';
import { DirectionsCar } from '@mui/icons-material';
import { format } from 'date-fns';
import { Ticket, TicketAttachment } from '../../types/ticket';
import { stateProvinceList } from '../../data/stateProvinceList';
import { useTicketTypeMeta } from '../../context/TicketTerminologyProvider';
import { getAttachmentPreviewUrl } from '../../utils/getAttachmentPreviewUrl';
import { ParkingLocation } from '../../types/parkingLocation';

interface Props {
	ticket: Ticket;
	onClick: (ticket: Ticket) => void;
	parkingLocation?: ParkingLocation;
}

const isImage = (att: TicketAttachment) =>
	att.ContentType?.toLowerCase().includes('image');

const TicketGridCard: React.FC<Props> = ({
	ticket,
	onClick,
	parkingLocation,
}) => {
	const {
		ticketType,
		licenseNumber,
		licenseState,
		date,
		vehicleInfo,
		parkingZone,
		attachments,
		isVoid,
	} = ticket;

	const getTicketTypeMeta = useTicketTypeMeta();
	const coverImage = (attachments ?? []).find(isImage);
	const stateObj = stateProvinceList.find((s) => s.code === licenseState);
	const licenseStateLabel = stateObj ? stateObj.label : licenseState;
	const { shortLabel, buttonColor } = getTicketTypeMeta(ticketType);

	const isoDate = new Date(date);
	const dateLine = format(isoDate, "EEE, dd MMM yyyy '·' hh:mm a");

	const detailLine =
		vehicleInfo?.trim() || parkingZone?.trim() || '';
	const isExpiryLot =
		parkingLocation?.violationCheckType === 'hourlyExpiry' &&
		!!parkingLocation?.maxStayMinutes;
	const isExpired =
		isExpiryLot &&
		Date.now() >=
			isoDate.getTime() + (parkingLocation?.maxStayMinutes ?? 0) * 60_000;

	return (
		<Card
			elevation={1}
			sx={{
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				transition: (theme) =>
					theme.transitions.create('box-shadow'),
				'&:hover': {
					boxShadow: 6,
				},
				opacity: isVoid ? 0.85 : 1,
			}}
		>
			<CardActionArea
				onClick={() => onClick(ticket)}
				sx={{
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'stretch',
				}}
			>
				<Box
					sx={{
						position: 'relative',
						width: '100%',
						aspectRatio: '16 / 10',
						bgcolor: 'action.hover',
						overflow: 'hidden',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					{coverImage ? (
						<Box
							component="img"
							src={getAttachmentPreviewUrl(coverImage.AttachmentID)}
							alt={`Record ${licenseNumber}`}
							loading="lazy"
							sx={{
								width: '100%',
								height: '100%',
								objectFit: 'cover',
								display: 'block',
							}}
						/>
					) : (
						<DirectionsCar
							sx={{ fontSize: 64, color: 'action.disabled' }}
						/>
					)}
					<Box
						sx={{
							position: 'absolute',
							top: 8,
							right: 8,
							display: 'flex',
							gap: 0.5,
							flexWrap: 'wrap',
							justifyContent: 'flex-end',
						}}
					>
						{isExpiryLot && (
							<Chip
								label={isExpired ? 'Expired' : 'Active'}
								color={isExpired ? 'error' : 'success'}
								variant="outlined"
								size="small"
								sx={{ bgcolor: 'background.paper' }}
							/>
						)}
						<Chip
							label={shortLabel}
							color={buttonColor}
							size="small"
						/>
					</Box>
					{isVoid && (
						<Chip
							label="Void"
							size="small"
							sx={{ position: 'absolute', top: 8, left: 8 }}
						/>
					)}
				</Box>
				<CardContent sx={{ width: '100%', flexGrow: 1 }}>
					<Stack spacing={0.5} sx={{ minWidth: 0 }}>
						<Typography
							variant="h6"
							fontWeight={600}
							noWrap
							sx={{ letterSpacing: '0.04em' }}
						>
							{licenseNumber || '—'}
						</Typography>
						<Typography
							variant="caption"
							color="text.secondary"
							noWrap
						>
							{licenseStateLabel || ' '}
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							noWrap
						>
							{dateLine}
						</Typography>
						{detailLine && (
							<Typography
								variant="caption"
								color="text.secondary"
								noWrap
							>
								{detailLine}
							</Typography>
						)}
					</Stack>
				</CardContent>
			</CardActionArea>
		</Card>
	);
};

export default TicketGridCard;
